import { useEffect, useState } from "react";
import { ArrowRight, X } from "@phosphor-icons/react";
import { HUNT_HUB_URL } from "@/lib/links";

/**
 * The site-wide launch banner. On desktop the navbar floats at `top-8`, so a
 * 2rem bar fixed at the top of the viewport sits exactly in that gap; on
 * mobile the navbar is sticky at the top, so the banner stays in the flow
 * above it and scrolls away.
 *
 * Dismissal is remembered per browser under a key that names the campaign, so
 * a future banner shows again without anyone clearing storage. Storage can be
 * unavailable (private windows, blocked site data); then the banner simply
 * shows on every visit.
 */
const CAMPAIGN = "huntbase-hub-launch";
const STORAGE_KEY = `hb-banner-dismissed:${CAMPAIGN}`;

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function AnnouncementBanner() {
  // Render nothing until the effect has read storage, so a dismissed banner
  // never flashes on load.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!readDismissed());
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // Nothing to do: the banner will show again next visit.
    }
  };

  return (
    <div
      data-cta-placement="announcement-banner"
      className="relative z-[60] flex h-8 items-center justify-center gap-3 bg-ink px-10 text-center md:fixed md:inset-x-0 md:top-0"
      role="region"
      aria-label="Announcement"
    >
      <a
        href={`${HUNT_HUB_URL}&utm_campaign=hub-launch&utm_content=announcement-banner`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-w-0 items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-indigo-300 transition-colors hover:text-white"
      >
        <span className="hidden rounded bg-primary-500/30 px-1.5 py-0.5 text-white sm:inline">
          New
        </span>
        <span className="sm:hidden">The Huntbase Hub is open</span>
        <span className="hidden truncate sm:inline">
          The Huntbase Hub is open: more than 240 published hunt playbooks, open to read and run
        </span>
        <ArrowRight size={12} weight="bold" className="shrink-0" />
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-indigo-300 transition-colors hover:text-white"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
